import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import RightMenu from '../../atoms/RightMenu/RightMenu';
import { removeApplianceFromActuator } from '../../Functions/ActuatorChannels';
import { capitalize } from '../../Functions/Capitalize';
import { disconnectActuatorFromDetector } from '../../Functions/DetectorConnections';
import { exportConfigurationToPdf } from '../../Functions/ExportToPdf';
import { formatEntityId, formatOptionalEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { setSelectedAppliance } from '../../redux/reducers/applianceReducer';
import { 
    disconnectControllerFromAllAppliances,
    setActuators,
    setApplianceLabel,
    setAppliances,
    setDetectors,
    setHoveredItem,
    setOtherDevices,
    setShowActuatorDetail,
    setShowApplianceDetail,
    setShowDetectorDetail,
    setShowDeviceDetail,
    setShowThermostatDetail,
    toggleRightMenu
} from '../../redux/reducers/appReducer';
import { removeActuatorFromAllControllers, removeApplianceFromAllControllers, removeDevice, setSelectedDeviceId, syncControllerChannelConnections } from '../../redux/reducers/controllersReducer';
import { setSelectedDetector } from '../../redux/reducers/detectorsReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';

import styles from '../../atoms/RightMenu/RightMenu.module.scss';

const RightMenuComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [rightMenuOpen, setRightMenuOpen] = useState(false);
    const [editingApplianceId, setEditingApplianceId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const appliances = useAppSelector((state) => state.app.appliances);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const actuators = useAppSelector((state) => state.app.actuators);
    const detectors = useAppSelector((state) => state.app.detectors);
    const otherDevices = useAppSelector((state) => state.app.otherDevices);
    const controllerType = useAppSelector((state) => state.app.controllerType);
    const [appliancesCollapsed, setAppliancesCollapsed] = useState(true);
    const [devicesCollapsed, setDevicesCollapsed] = useState(true);
    const [actuatorsCollapsed, setActuatorsCollapsed] = useState(true);
    const [detectorsCollapsed, setDetectorsCollapsed] = useState(true);
    const [othersCollapsed, setOthersCollapsed] = useState(true);
    const [dinDevicesCollapsed, setDinDevicesCollapsed] = useState(true);
    const totalDINModules = useAppSelector((state) => state.app.totalDINModules);
    const sumPrices = (items: Array<{ price?: number }>) => items.reduce((total, item) => total + (item.price ?? 0), 0);
    const formatPrice = (price: number) => `${price.toFixed(2)} €`;
    const globalTotal = sumPrices([...controllers, ...actuators, ...detectors, ...otherDevices]);
    const subtotal = (category: string, amount: number) => (
        <div className={styles.rightMenu__subtotal} data-testid={`right-menu-subtotal-${category}`}>
            <span>{t('rightMenu.pdf.total')}</span>
            <span>{formatPrice(amount)}</span>
        </div>
    );

    const handleMenuClick = () => {
        dispatch(toggleRightMenu());
        setRightMenuOpen(!rightMenuOpen);
    };
    const showDeviceDetail = (deviceId: number, deviceType: string) => {
        dispatch(setSelectedDeviceId(deviceId));
        switch(deviceType){
            case 'thermostat':
                dispatch(setShowThermostatDetail(true));
            break;
            case 'controller':
                dispatch(setShowDeviceDetail(true));
            break;
            default:
                toast.error(t('rightMenu.devices.error'));
            break;
        }
    }
    const startEditLabel = (applianceId: number, currentLabel: string) => {
        setEditingApplianceId(applianceId);
        setEditValue(currentLabel);
    }
    const saveLabel = (applianceId: number) => {
        if (editingApplianceId !== applianceId) {
            return;
        }

        dispatch(setApplianceLabel({ id: applianceId, label: editValue }));
        setEditingApplianceId(null);
        setEditValue('');
    }
    const handleEditKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, applianceId: number) => {
        if (event.key === 'Enter') {
            saveLabel(applianceId);
        }
    }
    const deleteOtherDevice = (deviceId: number) => {
        dispatch(setOtherDevices(otherDevices.filter((device) => device.id !== deviceId)));
    }
    const deleteAppliance = (applianceId: number) => {
        dispatch(removeApplianceFromAllControllers(applianceId));
        dispatch(setActuators(actuators.map((actuator) => removeApplianceFromActuator(actuator, applianceId))));
        dispatch(setAppliances(appliances.filter((appliance) => appliance.id !== applianceId)));
        dispatch(setShowApplianceDetail(false));
        dispatch(setHoveredItem(null));
        if (editingApplianceId === applianceId) {
            setEditingApplianceId(null);
            setEditValue('');
        }
    }
    const deleteActuators = (actuatorIds: number[]) => {
        if (actuatorIds.length === 0) {
            return;
        }
        const remainingActuators = actuators.filter((actuator) => !actuatorIds.includes(actuator.id ?? -1));
        actuatorIds.forEach((id) => dispatch(removeActuatorFromAllControllers(id)));
        dispatch(setActuators(remainingActuators));
        dispatch(syncControllerChannelConnections(remainingActuators));
        dispatch(setAppliances(appliances.map((appliance) => actuatorIds.includes(appliance.actuator ?? -1)
            ? { ...appliance, actuator: undefined }
            : appliance)));
        dispatch(setDetectors(detectors.map((detector) => actuatorIds.reduce(
            (updatedDetector, id) => disconnectActuatorFromDetector(updatedDetector, id), detector
        ))));
        dispatch(setShowActuatorDetail(false));
        dispatch(setHoveredItem(null));
    }
    const deleteController = (controllerId: number) => {
        deleteActuators(actuators.filter((actuator) => actuator.builtIn && actuator.linkedControllerId === controllerId)
            .flatMap((actuator) => actuator.id === undefined ? [] : [actuator.id]));
        dispatch(disconnectControllerFromAllAppliances(controllerId));
        dispatch(removeDevice(controllerId));
        dispatch(setShowDeviceDetail(false));
        dispatch(setShowThermostatDetail(false));
    }
    const deleteDetector = (detectorId: number) => {
        dispatch(setDetectors(detectors.filter((detector) => detector.id !== detectorId)));
        dispatch(setShowDetectorDetail(false));
        dispatch(setHoveredItem(null));
    }
    const deleteButton = (itemId: number | undefined, onDelete: (id: number) => void) => (
        <button
            type="button"
            className={styles.rightMenu__delete}
            aria-label={t('saveDetail.header.delete')}
            title={t('saveDetail.header.delete')}
            disabled={itemId === undefined}
            onClick={(event) => {
                event.stopPropagation();
                if (itemId !== undefined) {
                    onDelete(itemId);
                }
            }}
        >
            <img src={getIconPath('delete')} alt="" />
        </button>
    );
    const handleExportPdf = async () => {
        try {
            await exportConfigurationToPdf(controllers, actuators, detectors, otherDevices, appliances, capitalize(controllerType), {
                title: t('rightMenu.pdf.title'),
                controllersHeading: t('rightMenu.devices.heading'),
                actuatorsHeading: t('rightMenu.actuators.heading'),
                detectorsHeading: t('rightMenu.detectors.heading'),
                othersHeading: t('rightMenu.others.heading'),
                appliancesHeading: t('rightMenu.appliances.heading'),
                id: t('rightMenu.devices.header.ID'),
                type: t('rightMenu.devices.header.type'),
                name: t('rightMenu.pdf.description'),
                price: t('rightMenu.devices.header.price'),
                description: t('rightMenu.others.header.description'),
                total: t('rightMenu.pdf.total'),
                grandTotal: t('rightMenu.pdf.grandTotal'),
                technology: t('rightMenu.pdf.technology'),
                date: t('rightMenu.pdf.date'),
                noItems: t('rightMenu.pdf.noItems'),
                floorplanHeading: t('rightMenu.pdf.floorplanHeading'),
                distributionBoardHeading: t('electrical_cabinet.heading'),
                dinDevicesHeading: t('rightMenu.dinDevices.heading'),
                dinDevicesPriceNote: t('rightMenu.pdf.dinDevicesPriceNote')
            });
        } catch {
            toast.error(t('rightMenu.pdf.error'));
        }
    }

    const calculateModules = () => {
        const actuatorModules = actuators.filter(actuator => actuator.dinrail === true).reduce((total, actuator) => actuator.modules ? total + actuator.modules : total, 0);
        const otherModules = otherDevices.reduce((total, device) => total + (device.modules ?? 0), 0);
        return actuatorModules + otherModules;
    };

    return (
        <>
            <RightMenu rightMenuOpen={rightMenuOpen} onClick={handleMenuClick} onExport={handleExportPdf}>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.appliances.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setAppliancesCollapsed(!appliancesCollapsed)}/>
                    </div>
                    {!appliancesCollapsed && (
                        <div className={styles.rightMenu__content__appliances__header}>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.assignment')}</p></div>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.type')}</p></div>
                            <div></div>
                        </div>
                    )}
                    <RightMenu.Appliances rightMenuOpen={rightMenuOpen} collapsed={appliancesCollapsed}>
                        {appliances.map((item) => {
                            return (
                                <div
                                    className={styles.rightMenu__content__appliances__item}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'appliance', id: item.id, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <div className={styles.rightMenu__content__appliances__item__info}
                                        onClick={() => {dispatch(setSelectedAppliance({
                                            id: item.id,
                                            label: item.label,
                                            icon: item.icon,
                                            type: item.type,
                                            applianceType: item.applianceType,
                                            state: item.state ?? '',
                                            actuator: item.actuator ?? 0,
                                            connectedTo: item.connectedTo ?? [],
                                        }));
                                        dispatch(setShowApplianceDetail(true));}}
                                    >
                                        <img src={getIconPath(item.state || 'error')} alt="State" />
                                        <p>{formatEntityId('appliance', item.id)}</p>
                                        {editingApplianceId === item.id
                                            ? <input type='text' className={styles.rightMenu__content__appliances__item__input} autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => handleEditKeyDown(e, item.id)} onBlur={() => saveLabel(item.id)} />
                                            : <p>{item.label}</p>
                                        }
                                    </div>
                                    <img
                                        className={styles.rightMenu__content__appliances__item__edit}
                                        onClick={() => editingApplianceId === item.id ? saveLabel(item.id) : startEditLabel(item.id, item.label)}
                                        src={getIconPath('icoPen')}
                                        alt="Edit"
                                    />
                                    {deleteButton(item.id, deleteAppliance)}
                                </div>
                            );
                        })}
                    </RightMenu.Appliances>
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__devices__open : styles.rightMenu__content__devices__closed,styles.rightMenu__content__devices)}>
                    <div className={styles.rightMenu__content__devices__heading}>
                        <h3>{t('rightMenu.devices.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setDevicesCollapsed(!devicesCollapsed)} />
                    </div>
                    {!devicesCollapsed && (
                        <div className={classNames(styles.rightMenu__content__devices__header, styles.rightMenu__withDelete)}>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.state')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.type')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.price')}</p></div>
                            <div />
                        </div>
                    )}
                    <RightMenu.Devices rightMenuOpen={rightMenuOpen} collapsed={devicesCollapsed}>
                        {controllers.map((item) => {
                            return (
                                <div
                                    onClick={() => showDeviceDetail(item.id, item.type)}
                                    className={classNames(styles.rightMenu__content__devices__item, styles.rightMenu__withDelete)}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'controller', id: item.id, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatEntityId('controller', item.id)}</p>
                                    <p>{item.name + item.colorShortcut}</p>
                                    <p style={{ textAlign: 'right' }}>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, deleteController)}
                                </div>
                            )
                        })}
                    </RightMenu.Devices>
                    {subtotal('devices', sumPrices(controllers))}
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.actuators.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setActuatorsCollapsed(!actuatorsCollapsed)}/>
                    </div>
                    {!actuatorsCollapsed && (
                        <div className={classNames(styles.rightMenu__content__actuators__header, styles.rightMenu__withDelete)}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.state')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.price')}</p></div>
                            <div />
                        </div>
                    )}
                    <RightMenu.Actuators rightMenuOpen={rightMenuOpen} collapsed={actuatorsCollapsed}>
                        {actuators.map((item) => {
                            return (
                                <div
                                    onClick={() => {dispatch(setSelectedActuator(item));dispatch(setShowActuatorDetail(true));}}
                                    className={classNames(styles.rightMenu__content__actuators__item, styles.rightMenu__withDelete)}
                                    key={item.id}
                                    title={item.name}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'actuator', id: item.id || 0, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatOptionalEntityId('actuator', item.id)}</p>
                                    <p>{item.type}</p>
                                    <p style={{ textAlign: 'right' }}>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, (id) => deleteActuators([id]))}
                                </div>
                            )
                        })}
                    </RightMenu.Actuators>
                    {subtotal('actuators', sumPrices(actuators))}
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.detectors.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setDetectorsCollapsed(!detectorsCollapsed)}/>
                    </div>
                    {!detectorsCollapsed && (
                        <div className={classNames(styles.rightMenu__content__actuators__header, styles.rightMenu__withDelete)}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.state')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.price')}</p></div>
                            <div />
                        </div>
                    )}
                    <RightMenu.Detectors rightMenuOpen={rightMenuOpen} collapsed={detectorsCollapsed}>
                        {detectors.map((item) => {
                            return (
                                <div
                                    onClick={() => {dispatch(setSelectedDetector(item));dispatch(setShowDetectorDetail(true));}}
                                    className={classNames(styles.rightMenu__content__actuators__item, styles.rightMenu__withDelete)}
                                    key={item.id}
                                    title={item.label}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'detector', id: item.id || 0, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatOptionalEntityId('detector', item.id)}</p>
                                    <p>{item.model ?? item.name}</p>
                                    <p style={{ textAlign: 'right' }}>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, deleteDetector)}
                                </div>
                            )
                        })}
                    </RightMenu.Detectors>
                    {subtotal('detectors', sumPrices(detectors))}
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.others.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setOthersCollapsed(!othersCollapsed)}/>
                    </div>
                    {!othersCollapsed && (
                        <div className={classNames(styles.rightMenu__content__actuators__header, styles.rightMenu__withDelete, styles.rightMenu__withDeleteNoDescription)}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.others.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.others.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.others.header.price')}</p></div>
                            <div />
                        </div>
                    )}
                    <RightMenu.Others rightMenuOpen={rightMenuOpen} collapsed={othersCollapsed}>
                        {otherDevices.map((item) => {
                            return (
                                <div
                                    className={classNames(styles.rightMenu__content__actuators__item, styles.rightMenu__withDelete, styles.rightMenu__withDeleteNoDescription)}
                                    key={item.id}
                                    title={item.name}
                                >
                                    <p>{formatOptionalEntityId('other', item.id)}</p>
                                    <p>{item.type}</p>
                                    <p>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, deleteOtherDevice)}
                                </div>
                            )
                        })}
                    </RightMenu.Others>
                    {subtotal('others', sumPrices(otherDevices))}
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <span className={styles.rightMenu__content__appliances__heading__modules}><p>{t('rightMenu.dinDevices.modules')}: {calculateModules()}/{totalDINModules}</p></span>
                        <h3>{t('rightMenu.dinDevices.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setDinDevicesCollapsed(!dinDevicesCollapsed)}/>
                    </div>
                    {!dinDevicesCollapsed && (
                        <div className={classNames(styles.rightMenu__content__actuators__header, styles.rightMenu__withDelete, styles.rightMenu__withDeleteNoDescription)}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.dinDevices.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.dinDevices.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.dinDevices.header.price')}</p></div>
                            <div></div>
                        </div>
                    )}
                    <RightMenu.DINDevices rightMenuOpen={rightMenuOpen} collapsed={dinDevicesCollapsed} childrenCount={(actuators.filter(item => item.dinrail === true).length) + (otherDevices.length)}>
                        {actuators.map((item) => {
                            if(!item.dinrail) return null;
                            return (
                                <div
                                    onClick={() => {dispatch(setSelectedActuator(item));dispatch(setShowActuatorDetail(true));}}
                                    className={classNames(styles.rightMenu__content__actuators__item, styles.rightMenu__withDeleteDIN, styles.rightMenu__withDeleteNoDescription)}
                                    key={item.id}
                                    title={item.name}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'actuator', id: item.id || 0, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <p>{formatOptionalEntityId('actuator', item.id)}</p>
                                    <p>{item.type}</p>
                                    <p>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, (id) => deleteActuators([id]))}
                                </div>
                            )
                        })}
                        {otherDevices.map((item) => {
                            return (
                                <div
                                    className={classNames(styles.rightMenu__content__actuators__item, styles.rightMenu__withDeleteDIN, styles.rightMenu__withDeleteNoDescription)}
                                    key={item.id}
                                    title={item.name}
                                >
                                    <p>{formatOptionalEntityId('other', item.id)}</p>
                                    <p>{item.type}</p>
                                    <p>{item.price !== undefined ? Math.round(item.price * 10) / 10 : ''} {item.price ? '€' : ''}</p>
                                    {deleteButton(item.id, deleteOtherDevice)}
                                </div>
                            )
                        })}
                    </RightMenu.DINDevices>
                </div>
                {rightMenuOpen && (
                    <div className={styles.rightMenu__grandTotal} data-testid="right-menu-grand-total">
                        <span>{t('rightMenu.pdf.grandTotal')}</span>
                        <span>{formatPrice(globalTotal)}</span>
                    </div>
                )}
            </RightMenu>
        </>
    )
};

export default RightMenuComponent;